import React, { useState, lazy, Suspense } from 'react';
import PaymentsModule from '../components/PaymentsModule';
import { CreditCard, Gift, Coins, Receipt, TrendingDown } from 'lucide-react';

// Lazy load Gift Card module
const GiftCardManagementPage = lazy(() => import('./GiftCardManagementPage'));
// Lazy load Digital Coins page (real balance) — replaces the old hardcoded placeholder
const DigitalCoinsPage = lazy(() => import('./DigitalCoinsPage'));
// Lazy load Receipts (recent completed transactions, sourced from the shared receipts brain)
const ReceiptsTab = lazy(() => import('../components/ReceiptsTab'));
// Lazy load Expenses (shop-cost ledger — SLIK #4; manager/owner only)
const ExpensesTab = lazy(() => import('../components/ExpensesTab'));

const POSPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('checkout');

  const tabs = [
    { id: 'checkout', label: 'Checkout', icon: CreditCard },
    { id: 'giftcards', label: 'Gift Cards', icon: Gift },
    { id: 'expenses', label: 'Expenses', icon: TrendingDown },
    { id: 'digitalcoins', label: 'Digital Coins', icon: Coins },
    { id: 'receipts', label: 'Receipts', icon: Receipt },
  ];

  return (
    <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 shadow border-b dark:border-gray-700">
        <div className="p-4 md:p-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4">Register</h1>
          <div className="flex overflow-x-auto pb-2 -mb-2 gap-2 scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-3 rounded-xl transition-all whitespace-nowrap flex-shrink-0 touch-manipulation min-h-[44px] ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 bg-gray-50 dark:bg-gray-700/50'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  <span className="text-sm md:text-base font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        {/* PERSISTENCE (Jose 2026-08-18): keep the Register cart MOUNTED across tab switches. Previously
            this was `activeTab === 'checkout' && <PaymentsModule />`, which UNMOUNTED the module on every
            tab change and destroyed all its useState (cart, clients, walk-in name) — so an in-progress
            transaction reset. Hiding with CSS instead preserves the whole checkout while off-tab. */}
        <div className={activeTab === 'checkout' ? '' : 'hidden'}>
          <PaymentsModule />
        </div>
        {activeTab === 'giftcards' && (
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          }>
            <GiftCardManagementPage />
          </Suspense>
        )}
        {activeTab === 'expenses' && (
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          }>
            <ExpensesTab />
          </Suspense>
        )}
        {activeTab === 'digitalcoins' && (
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          }>
            {/* Wire the real DigitalCoinsPage (shows the actual balance) instead of the old hardcoded
                fake "2,500 in circulation" placeholder (Jose 2026-08-18). */}
            <DigitalCoinsPage />
          </Suspense>
        )}
        {activeTab === 'receipts' && (
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          }>
            <ReceiptsTab />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default POSPage;